// Copyright (c) Ping. All rights reserved.
//
// Receive-side authorship gate for message mutations (`com.ping.edit` /
// `com.ping.redact`). Kept as a standalone, dependency-light module (only the
// self-identity helpers) so the security-critical logic is unit-testable
// without dragging the React / RN incoming-message harness into the test.
//
// Rule (mirrors iOS `mutationSenderIsAuthor`): ONLY the target message's
// author — any device of that author's account — may edit or
// delete-for-everyone that message. The sender identity is the
// MLS-authenticated sender (device hex + optional account-level MLS UserId),
// NEVER a spoofable in-payload `by` field. Deny-by-default when identity is
// unresolvable. Admin/moderator redaction (§5.3.2) is not implemented on
// either client yet, so this is sender-only, matching iOS.

import {
  getCurrentUserAccountId,
  isSelfDeviceIdHex,
  isSelfMlsUserIdHex,
} from './registerCurrentUser';

/** Minimal shapes so this module doesn't depend on the full persistence types. */
export interface MutationTargetIdentity {
  readonly senderAccountId: string;
  readonly senderUserIdHex?: string;
}
export interface RosterCarrier {
  readonly memberDeviceAccounts?: Record<string, string>;
}

/** Resolve an MLS-authenticated identity (device hex + optional account-level
 *  UserId) to its ACCOUNT id: self when it's one of our own devices/identity,
 *  else the authenticated device→account roster on the conversation. Returns
 *  `undefined` when unresolvable (callers deny by default). */
export function resolveSenderAccount(
  deviceHex: string | undefined,
  userIdHex: string | undefined,
  conv: RosterCarrier | undefined,
): string | undefined {
  if (userIdHex !== undefined && userIdHex.length > 0 && isSelfMlsUserIdHex(userIdHex)) {
    return getCurrentUserAccountId() ?? undefined;
  }
  if (deviceHex !== undefined && deviceHex.length > 0 && isSelfDeviceIdHex(deviceHex)) {
    return getCurrentUserAccountId() ?? undefined;
  }
  if (deviceHex !== undefined && deviceHex.length > 0) {
    return conv?.memberDeviceAccounts?.[deviceHex];
  }
  return undefined;
}

/** True iff the MLS-authenticated sender of an edit/redact is the target
 *  message's author (any device of that author's account). */
export function mutationSenderIsAuthor(
  target: MutationTargetIdentity,
  senderDeviceHex: string,
  senderUserIdHex: string | undefined,
  conv: RosterCarrier | undefined,
): boolean {
  // Bulletproof, fetch-free path: with shared-identity linking both the mutation
  // and the target carry the account-level MLS UserId, directly comparable
  // across all of the author's devices.
  if (
    senderUserIdHex !== undefined &&
    senderUserIdHex.length > 0 &&
    target.senderUserIdHex !== undefined &&
    target.senderUserIdHex.length > 0
  ) {
    return senderUserIdHex.toLowerCase() === target.senderUserIdHex.toLowerCase();
  }
  // Fallback for legacy rows without a UserId: resolve both device hexes to
  // accounts via the authenticated roster (or self) and require the same account.
  const senderAccount = resolveSenderAccount(senderDeviceHex, senderUserIdHex, conv);
  const authorAccount = resolveSenderAccount(target.senderAccountId, target.senderUserIdHex, conv);
  return (
    senderAccount !== undefined &&
    senderAccount.length > 0 &&
    authorAccount !== undefined &&
    authorAccount.length > 0 &&
    senderAccount === authorAccount
  );
}
